import { appRoutes, type RouteName, type RouteParamsFor } from '../routes';

/**
 * Tail-only rest-args for `route(name, …)` / `navigate(name, …)`.
 *
 * Three cases, in order:
 *  - Route has no `params` field at all → no second arg.
 *  - Route has `params` but every key is optional (`{ tab? }`,
 *    `{ pay? }`) → second arg is optional.
 *  - Route has at least one required param → second arg is required.
 *
 * Splitting `name` out of the rest tuple keeps the inference clean
 * — TS reliably reduces the conditional against the concrete `T`
 * when it's only looking at the trailing args, where the
 * nested-conditional-inside-variadic form has been fragile in
 * practice (TS 5.4 produces "Expected 2 arguments" at the call
 * site even when the type theoretically widens).
 */
export type RouteRestArgs<T extends RouteName> =
  RouteParamsFor<T> extends undefined
    ? []
    : {} extends RouteParamsFor<T>
      ? [params?: RouteParamsFor<T>]
      : [params: RouteParamsFor<T>];

/**
 * Build a typed URL string for a given route name.
 *
 * - Replaces path params (`:id`) with actual values.
 * - Appends remaining params as search params.
 * - Routes whose params are **all optional** (e.g. `wallet`,
 *   `settings_profile`) can be called with no second argument —
 *   the signature widens automatically via `{} extends Params`.
 *
 * @example
 *   route('wallet')
 *   // => "/wallet"
 *
 *   route('wallet', { pay: 'advance' })
 *   // => "/wallet?pay=advance"
 *
 *   route('settings_profile')
 *   // => "/more/profile"
 *
 *   route('settings_profile', { tab: 'banking' })
 *   // => "/more/profile?tab=banking"
 *
 *   route('order_location_edit', { id: '123' })
 *   // => "/dashboard/order/location/edit/123"
 */
export function route<T extends RouteName>(
  name: T,
  ...rest: RouteRestArgs<T>
): string {
  const [params] = rest as [Record<string, unknown> | undefined];
  const def = appRoutes[name];
  let url = def.path;

  if (!params) return url;

  const searchEntries: [string, string][] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    const pathPlaceholder = `:${key}`;
    if (url.includes(pathPlaceholder)) {
      url = url.replace(pathPlaceholder, encodeURIComponent(String(value)));
    } else {
      const serialized = Array.isArray(value) ? value.join(',') : String(value);
      if (serialized) {
        searchEntries.push([key, serialized]);
      }
    }
  }

  if (searchEntries.length > 0) {
    const qs = new URLSearchParams(searchEntries).toString();
    url += `?${qs}`;
  }

  return url;
}
